import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { customizationApi } from '../services/customizationApi'
import { useAuth } from './AuthContext'

const ThemeContext = createContext(null)

const DEFAULT_THEME_CONFIG = {
  colors: {
    primary: '#5b21b6',
    secondary: '#3d1579',
    accent: '#d946ef',
    background: '#f9fafb',
    surface: '#ffffff',
    pageBackground: '#f9fafb',
    cardBackground: '#ffffff',
    cardBorder: '#e4e4e7',
    text: '#111827',
    textMuted: '#52525b',
    border: '#e4e4e7',
    gradientStart: '#4c1d95',
    gradientMid: '#2e1065',
    gradientEnd: '#1e0b3d',
  },
  typography: {
    fontFamily: 'Inter, system-ui, sans-serif',
    headingWeight: 700,
    bodyWeight: 400,
  },
  borders: {
    radiusSm: '0.5rem',
    radiusMd: '0.75rem',
    radiusLg: '1rem',
    cardBorderWidth: '1px',
    cardBorderStyle: 'solid',
    inputBorderWidth: '1px',
    buttonBorderStyle: 'solid',
    buttonBorderWidth: '1px',
    primaryButtonBorderWidth: '0px',
  },
  chrome: {
    menuBackgroundMode: 'gradient',
    menuSolidColor: '#4c1d95',
    menuTextColor: '#ffffff',
    menuActiveItemBg: '#ffffff',
    menuActiveItemText: '',
    buttonPrimaryMode: 'gradient',
    buttonPrimarySolidColor: '#5b21b6',
    buttonSecondaryMode: 'solid',
    buttonSecondarySolidColor: '#3d1579',
    iconBoxMode: 'light',
    iconBoxSolidColor: '#5b21b6',
    avatarShape: 'rounded',
    avatarBorderColor: '',
    avatarBorderWidth: '2px',
  },
}

function parseThemeConfig(configJson) {
  if (!configJson) return DEFAULT_THEME_CONFIG
  try {
    const parsed = JSON.parse(configJson)
    return {
      colors: { ...DEFAULT_THEME_CONFIG.colors, ...(parsed.colors || {}) },
      typography: { ...DEFAULT_THEME_CONFIG.typography, ...(parsed.typography || {}) },
      borders: { ...DEFAULT_THEME_CONFIG.borders, ...(parsed.borders || {}) },
      chrome: { ...DEFAULT_THEME_CONFIG.chrome, ...(parsed.chrome || {}) },
    }
  } catch {
    return DEFAULT_THEME_CONFIG
  }
}

const DEFAULT_FAVICON_HREF = '/mais_logo.png'

/** Rotas abertas em janela própria com favicon/título próprios — não sobrescrever com o do tenant. */
function isStandaloneToolDocumentPath(pathname) {
  if (!pathname) return false
  return (
    /^\/router-management\//.test(pathname) ||
    /^\/router-terminal\//.test(pathname) ||
    /^\/host-console\//.test(pathname) ||
    /^\/remote-display\//.test(pathname) ||
    /^\/device-console\//.test(pathname) ||
    /^\/devices\/[^/]+\/web-ui(\/|$)/.test(pathname)
  )
}

function applyTenantFavicon(faviconBase64, faviconUrl) {
  if (typeof document === 'undefined') return
  if (typeof window !== 'undefined' && isStandaloneToolDocumentPath(window.location.pathname)) return
  const b64 = faviconBase64 && String(faviconBase64).trim()
  const url = faviconUrl && String(faviconUrl).trim()
  const href = b64 || url || null

  let link = document.querySelector('link[rel="icon"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }

  if (!href) {
    link.removeAttribute('type')
    link.href = DEFAULT_FAVICON_HREF
    return
  }

  let mime = 'image/png'
  if (href.startsWith('data:')) {
    const m = href.match(/^data:([^;,]+)/)
    if (m) mime = m[1]
  } else if (/\.svg(\?|#|$)/i.test(href)) mime = 'image/svg+xml'
  else if (/\.ico(\?|#|$)/i.test(href)) mime = 'image/x-icon'
  else if (/\.gif(\?|#|$)/i.test(href)) mime = 'image/gif'
  else if (/\.webp(\?|#|$)/i.test(href)) mime = 'image/webp'

  link.type = mime
  link.href = href
}

function normalizeBorderStyle(v, fallback = 'solid') {
  const s = String(v || '').toLowerCase().trim()
  if (['solid', 'dashed', 'dotted', 'double', 'none'].includes(s)) return s
  return fallback
}

function applyChromeVars(root, chrome, colors) {
  const ch = { ...DEFAULT_THEME_CONFIG.chrome, ...(chrome || {}) }
  const gs = colors.gradientStart || '#4c1d95'
  const gm = colors.gradientMid || '#2e1065'
  const ge = colors.gradientEnd || '#1e0b3d'
  const menuBg =
    ch.menuBackgroundMode === 'solid'
      ? ch.menuSolidColor || colors.primary
      : `linear-gradient(145deg, ${gs}, ${gm}, ${ge})`
  root.style.setProperty('--theme-menu-background', menuBg)
  root.style.setProperty('--theme-menu-text', ch.menuTextColor || '#ffffff')
  root.style.setProperty('--theme-menu-active-bg', ch.menuActiveItemBg || '#ffffff')
  root.style.setProperty('--theme-menu-active-text', ch.menuActiveItemText || '')

  if (ch.buttonPrimaryMode === 'solid') root.dataset.buttonPrimaryMode = 'solid'
  else delete root.dataset.buttonPrimaryMode
  if (ch.buttonSecondaryMode === 'gradient') root.dataset.buttonSecondaryMode = 'gradient'
  else delete root.dataset.buttonSecondaryMode
  root.style.setProperty('--theme-button-primary-solid', ch.buttonPrimarySolidColor || colors.primary)
  root.style.setProperty('--theme-button-secondary-solid', ch.buttonSecondarySolidColor || colors.secondary)

  const ibMode = ch.iconBoxMode || 'light'
  if (ibMode === 'gradient') {
    root.style.setProperty('--theme-icon-box-background', `linear-gradient(145deg, ${gs}, ${gm}, ${ge})`)
    root.style.setProperty('--theme-icon-box-text', '#ffffff')
  } else if (ibMode === 'solid') {
    root.style.setProperty('--theme-icon-box-background', ch.iconBoxSolidColor || colors.primary)
    root.style.setProperty('--theme-icon-box-text', '#ffffff')
  } else {
    root.style.setProperty('--theme-icon-box-background', '')
    root.style.setProperty('--theme-icon-box-text', '')
  }

  if (ch.avatarShape === 'circle') root.dataset.avatarShape = 'circle'
  else delete root.dataset.avatarShape
  root.style.setProperty('--theme-avatar-border-color', ch.avatarBorderColor || colors.primary)
  root.style.setProperty('--theme-avatar-border-width', ch.avatarBorderWidth || '2px')
}

function applyThemeCssVars(themeConfig) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const { colors, typography, borders, chrome } = themeConfig

  const pageBg = colors.pageBackground || colors.background
  const cardBg = colors.cardBackground || colors.surface
  const cardBorderCol = colors.cardBorder || colors.border

  root.style.setProperty('--theme-primary', colors.primary)
  root.style.setProperty('--theme-secondary', colors.secondary)
  root.style.setProperty('--theme-accent', colors.accent)
  root.style.setProperty('--theme-background', colors.background)
  root.style.setProperty('--theme-surface', colors.surface)
  root.style.setProperty('--theme-page-background', pageBg)
  root.style.setProperty('--theme-card-background', cardBg)
  root.style.setProperty('--theme-card-border-color', cardBorderCol)
  root.style.setProperty('--theme-text', colors.text)
  root.style.setProperty('--theme-text-muted', colors.textMuted)
  root.style.setProperty('--theme-border', colors.border)
  root.style.setProperty('--theme-gradient-start', colors.gradientStart || '#4c1d95')
  root.style.setProperty('--theme-gradient-mid', colors.gradientMid || '#2e1065')
  root.style.setProperty('--theme-gradient-end', colors.gradientEnd || '#1e0b3d')

  root.style.setProperty('--theme-font-family', typography.fontFamily)
  root.style.setProperty('--theme-heading-weight', String(typography.headingWeight))
  root.style.setProperty('--theme-body-weight', String(typography.bodyWeight))

  root.style.setProperty('--theme-radius-sm', borders.radiusSm)
  root.style.setProperty('--theme-radius-md', borders.radiusMd)
  root.style.setProperty('--theme-radius-lg', borders.radiusLg)
  root.style.setProperty('--theme-card-border-width', borders.cardBorderWidth)
  root.style.setProperty('--theme-card-border-style', normalizeBorderStyle(borders.cardBorderStyle))
  root.style.setProperty('--theme-input-border-width', borders.inputBorderWidth)
  root.style.setProperty('--theme-button-border-style', normalizeBorderStyle(borders.buttonBorderStyle))
  root.style.setProperty('--theme-button-border-width', borders.buttonBorderWidth || '1px')
  root.style.setProperty('--theme-primary-button-border-width', borders.primaryButtonBorderWidth ?? '0px')

  applyChromeVars(root, chrome, colors)
}

const LEGACY_CACHE_KEY = 'automais-theme-cache'
const LAST_TENANT_KEY = 'automais-theme-cache:last-tenant-id'
const tenantCacheKey = (tenantId) => `automais-theme-cache:t:${tenantId}`

// Customization payload (logo + favicon + flags) cached so BrandLogo and the
// favicon don't flash defaults while the customization API call is in flight.
const LEGACY_CUSTOMIZATION_KEY = 'automais-customization-cache'
const tenantCustomizationKey = (tenantId) => `automais-customization-cache:t:${tenantId}`

function readRawCache(key) {
  try { return localStorage.getItem(key) } catch { return null }
}

function readCachedConfig(tenantId) {
  // Prefer per-tenant cache; fall back to last-applied (used as the optimistic
  // pre-DOM theme by index.html).
  const keyed = tenantId ? readRawCache(tenantCacheKey(tenantId)) : null
  return keyed || readRawCache(LEGACY_CACHE_KEY)
}

function readCachedCustomization(tenantId) {
  const raw =
    (tenantId && readRawCache(tenantCustomizationKey(tenantId))) ||
    readRawCache(LEGACY_CUSTOMIZATION_KEY)
  if (!raw) return null
  try { return JSON.parse(raw) } catch { return null }
}

function applyCachedTheme(tenantId) {
  const raw = readCachedConfig(tenantId)
  if (!raw) return false
  applyThemeCssVars(parseThemeConfig(raw))
  return true
}

function persistCache(rawJson, tenantId, customizationData) {
  try {
    if (rawJson) {
      // Legacy single-key kept so the pre-DOM script in index.html can apply
      // *something* without knowing which tenant comes back. The keyed entry
      // is the source of truth on subsequent loads of the same tenant.
      localStorage.setItem(LEGACY_CACHE_KEY, rawJson)
      if (tenantId) localStorage.setItem(tenantCacheKey(tenantId), rawJson)
    }
    if (customizationData) {
      const json = JSON.stringify(customizationData)
      localStorage.setItem(LEGACY_CUSTOMIZATION_KEY, json)
      if (tenantId) localStorage.setItem(tenantCustomizationKey(tenantId), json)
    }
    if (tenantId) localStorage.setItem(LAST_TENANT_KEY, String(tenantId))
  } catch { /* ignore */ }
}

export function ThemeProvider({ children }) {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth()
  // Hydrate from cache so BrandLogo / favicon don't flash defaults while the
  // customization API call is in flight on Ctrl+F5 or logout/login.
  const [customization, setCustomization] = useState(() => readCachedCustomization(null))
  // Render right away if we already have a cache (inline script applied it) or
  // the user is not authenticated. Block only when we *know* a tenant theme is
  // about to arrive but we have nothing on screen yet.
  const [themeReady, setThemeReady] = useState(() => !!readRawCache(LEGACY_CACHE_KEY))
  const [isLoading, setIsLoading] = useState(false)

  // Apply the cached favicon synchronously on mount so the browser tab icon
  // doesn't revert to the Automais default during the round-trip.
  useEffect(() => {
    const cached = readCachedCustomization(null)
    if (cached) {
      applyTenantFavicon(
        cached.faviconBase64 || cached.FaviconBase64,
        cached.faviconUrl || cached.FaviconUrl,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadCustomization = async () => {
    const tenantId = user?.tenantId || user?.TenantId || null

    if (!isAuthenticated) {
      // Do NOT wipe to defaults if we have a cache — keeps the previous tenant's
      // theme + logo + favicon alive across logout so the login page doesn't
      // flash Automais branding.
      const cachedCustomization = readCachedCustomization(null)
      if (cachedCustomization) {
        setCustomization(cachedCustomization)
        applyTenantFavicon(
          cachedCustomization.faviconBase64 || cachedCustomization.FaviconBase64,
          cachedCustomization.faviconUrl || cachedCustomization.FaviconUrl,
        )
      } else {
        setCustomization(null)
        applyTenantFavicon(null, null)
      }
      if (!applyCachedTheme(null)) applyThemeCssVars(DEFAULT_THEME_CONFIG)
      setThemeReady(true)
      return
    }

    // Authenticated: apply tenant-specific cache (or legacy fallback) synchronously
    // before the API call so we never paint defaults during the round-trip.
    // Only release the render gate once we have *something* on screen — if the
    // cache hit, immediately; otherwise wait for the API (rare first-time path).
    const cachedCustomization = readCachedCustomization(tenantId)
    if (cachedCustomization) {
      setCustomization(cachedCustomization)
      applyTenantFavicon(
        cachedCustomization.faviconBase64 || cachedCustomization.FaviconBase64,
        cachedCustomization.faviconUrl || cachedCustomization.FaviconUrl,
      )
    }
    if (applyCachedTheme(tenantId)) setThemeReady(true)

    setIsLoading(true)
    try {
      const data = await customizationApi.getCurrent()
      setCustomization(data)
      const configJsonStr = data?.effectiveSchemeConfigJson || data?.EffectiveSchemeConfigJson
      const parsed = parseThemeConfig(configJsonStr)
      applyThemeCssVars(parsed)
      persistCache(configJsonStr || null, tenantId, data)
      applyTenantFavicon(
        data?.faviconBase64 || data?.FaviconBase64,
        data?.faviconUrl || data?.FaviconUrl,
      )
    } catch {
      // On error keep the cached customization (already applied above); only
      // fall back to defaults if no cache exists.
      if (!cachedCustomization) setCustomization(null)
      if (!applyCachedTheme(tenantId)) applyThemeCssVars(DEFAULT_THEME_CONFIG)
      if (!cachedCustomization) applyTenantFavicon(null, null)
    } finally {
      setIsLoading(false)
      setThemeReady(true)
    }
  }

  useEffect(() => {
    // Wait for auth to settle. Without this, the first render fires with
    // isAuthenticated=false (token still being read from localStorage) and the
    // unauthenticated branch wipes the cache the inline script just applied.
    if (authLoading) return
    loadCustomization()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, user?.tenantId, user?.TenantId, user?.id, user?.Id])

  const value = useMemo(
    () => ({
      customization,
      isLoading,
      themeReady,
      reloadCustomization: loadCustomization,
      updateLogo: async ({ logoUrl, logoBase64 }) => {
        await customizationApi.updateLogo({ logoUrl, logoBase64 })
        await loadCustomization()
      },
      updateLogoMenuBackdrop: async ({ mode, color }) => {
        await customizationApi.updateLogoMenuBackdrop({ mode, color })
        await loadCustomization()
      },
      updateFavicon: async ({ faviconUrl, faviconBase64 }) => {
        await customizationApi.updateFavicon({ faviconUrl, faviconBase64 })
        await loadCustomization()
      },
      createScheme: async (payload) => {
        const result = await customizationApi.createScheme(payload)
        await loadCustomization()
        return result
      },
      setTenantDefault: async (schemeId) => {
        await customizationApi.setTenantDefault(schemeId)
        await loadCustomization()
      },
      setMyTheme: async (schemeId) => {
        await customizationApi.setMyTheme(schemeId)
        await loadCustomization()
      },
      updateScheme: async (schemeId, payload) => {
        const result = await customizationApi.updateScheme(schemeId, payload)
        await loadCustomization()
        return result
      },
    }),
    [customization, isLoading, themeReady],
  )

  if (!themeReady) {
    return (
      <ThemeContext.Provider value={value}>
        <div style={{ visibility: 'hidden', position: 'fixed' }} aria-hidden="true" />
      </ThemeContext.Provider>
    )
  }

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme deve ser usado dentro de ThemeProvider')
  }
  return context
}

